import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { CameraService } from "./camera.service";
import { CoreService } from "./core.service";
import { DataContextService } from "./data-context.service";
import { ShapeMapEntity } from "./shape-map-entity.service";
import { WindowService } from "./window.service";
import { TimelineService } from "./timeline.service";

import * as Cesium from 'cesium';

export class PolylineMapEntity extends ShapeMapEntity {
  locations: any[];
  altitude: number | undefined;
  material: any;
  width: number | undefined;
  centerLatitude: number | undefined;
  centerLongitude: number | undefined;

  constructor(
    mapEntityName: string,
    mapEntityLayerPath: string,
    cameraService : CameraService,
    coreService: CoreService,
    locations: any[],
    material: any,
    width: number,
    windowService: WindowService,
    dataContext: DataContextService,
    modalService: NgbModal,
    timelineService: TimelineService,
    altitude?: number,
  ) {
    super(mapEntityName,mapEntityLayerPath,cameraService,coreService,windowService,dataContext,modalService,timelineService);
    this.locations = locations;
    this.altitude = altitude;
    this.material = material;
    this.width = width;

    if (locations !== undefined) {
      const centerLocations = center(locations);
      if (centerLocations && centerLocations.length === 2) {
        this.setCenterLatitude(centerLocations[0]);
        this.setCenterLongitude(centerLocations[1]);
      }
    }
  }
  override setCenterLatitude(lat: number) {
    // Implement as needed, or just store the value
    this.centerLatitude = lat;
  }
  override setCenterLongitude(lon: number) {
    // Implement as needed, or just store the value
    this.centerLongitude = lon;
  }

  setMaterial(material: any): void {
    this.material = material;
    if (this.cesiumEntity?.polyline) {
      this.cesiumEntity.polyline.material = new Cesium.ColorMaterialProperty(this.material);
    }
  }

  getMaterial(): any {
    return this.material;
  }

  setWidth(width: number): void {
    this.width = width;
    if (this.cesiumEntity?.polyline) {
      this.cesiumEntity.polyline.width = this.width;
    }
  }

  override setName(name: string): void {
    this.name = name;
  }

  setCoordinates(locations: any[]): void {
    this.locations = locations;
    const centerLocations = center(locations);
    if (centerLocations && centerLocations.length === 2) {
      this.setCenterLatitude(centerLocations[0]);
      this.setCenterLongitude(centerLocations[1]);
    }
    if (this.cesiumEntity?.polyline) {
      this.cesiumEntity.polyline.positions = Cesium.Cartesian3.fromDegreesArrayHeights(
        this.arrayOfLocationsToArrayOfPoints(this.locations)
      );
    }
  }

  getCoordinates(): any[] {
    return this.locations;
  }

  setAltitude(altitude: number): void {
    this.altitude = altitude;
    if (this.cesiumEntity?.polyline) {
      this.cesiumEntity.polyline.positions = Cesium.Cartesian3.fromDegreesArrayHeights(
        this.arrayOfLocationsToArrayOfPoints(this.locations)
      );
    }
  }

  getAltitude(): number | undefined {
    return this.altitude;
  }

  arrayOfLocationsToArrayOfPoints(locations: any[]): number[] {
    const arrayOfPoints: number[] = [];
    for (let i = 0; i < locations.length; i++) {
      arrayOfPoints.push(locations[i][1]);
      arrayOfPoints.push(locations[i][0]);
      arrayOfPoints.push(this.altitude ?? 0);
    }
    return arrayOfPoints;
  }

  override buildEntity(): void {
    super.buildEntity();
    if (!this.cesiumEntity) {
      this.cesiumEntity = {};
    }
    const points = this.arrayOfLocationsToArrayOfPoints(this.locations);
    this.cesiumEntity.polyline = {
      positions: Cesium.Cartesian3.fromDegreesArrayHeights(points)
    };
    this.cesiumEntity.polyline.material = new Cesium.ColorMaterialProperty(this.material);
    this.cesiumEntity.polyline.width = this.width;

    if (this.timestamp !== undefined) {
      const startDate = Cesium.JulianDate.fromDate(this.timestamp);
      const endDate = new Cesium.JulianDate(3448257, 0); // 1000 years in future
      this.cesiumEntity.availability = new Cesium.TimeIntervalCollection([
        new Cesium.TimeInterval({ start: startDate, stop: endDate })
      ]);
    }
  }

  override getCesiumEntity(): any {
    if (this.cesiumEntity === undefined) {
      this.buildEntity();
    }
    return this.cesiumEntity;
  }

  override setShowInOverlappingEntitiesUI(value: boolean): void {
    // Implement as needed, or leave empty if not used
  }

  override setShowIn2d(value: boolean): void {
    // Implement as needed, or leave empty if not used
  }

  override setWindowId(windowId: string): void {
    (this as any).windowId = windowId;
  }

   override setAddedToMap(status: boolean): void {
    this.addedToMap = status;
  }
}

function center(locations: any[]): [number, number] | undefined {
  if (locations && locations.length > 0) {
    return [locations[0][0], locations[0][1]];
  }
  return undefined;
}